import { Grid, Paper, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IJobBoard, amplitude } from 'wisestep-api';
import {
  CardVariant,
  WsButton,
  WsCard,
  SettingsIconAlt,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  IUserDTO,
} from 'wisestep-base';

import {
  useUserContext,
  useWorkspaceContext,
  useWorkspaceSKUContext,
} from '../../../../../../src/context';
import { ActivateFeature } from '../../../../../components/ActivateFeature';
import CareerBuilderIcon from '../../../../../images/jobBoardsIcons/CareerBuilder.png';
import CwJobsIcon from '../../../../../images/jobBoardsIcons/CWJob.png';
import DiceIcon from '../../../../../images/jobBoardsIcons/Dice.png';
import MonsterIcon from '../../../../../images/jobBoardsIcons/Monster.png';
import TechFetchIcon from '../../../../../images/jobBoardsIcons/TechFetch.png';
import PublishAddAccountDrawer from '../configureAccounts/PublishAddAccountDrawer';
import { getJobBoards, IJobData } from '../helper/jobBoardHelper';

export interface IJobBoardNameAccount {
  name: string[];
  accounts: number;
}
const StyledButton = styled(WsButton)({
  ':hover': {
    backgroundColor: WsColors.TheBlues900,
  },
});

interface IPremiumJobBoardAccountsState {
  Monster: IJobBoard[];
  Dice: IJobBoard[];
  TechFetch: IJobBoard[];
  CareerBuilder: IJobBoard[];
  CWJobs: IJobBoard[];
}

const PremiumJobBoardAccounts = ({ jobData }: IJobData) => {
  const workspaceSKU = useWorkspaceSKUContext();
  const premiumJobPostingSku = workspaceSKU?.workspacePlan?.workspacePlanFeatures
    ? workspaceSKU.workspacePlan.workspacePlanFeatures.filter(
        (data) => data.featureCode === 'JS_EXTERNAL_JOB_POSTING_PREMIUM'
      )
    : [];
  const showPremiumJobPosting = premiumJobPostingSku?.[0]?.displayWhenInactive;
  const navigate = useNavigate();

  const [premiumJobBoardAccountResponse, setPremiumJobBoardAccountResponse] =
    useState<IPremiumJobBoardAccountsState>({
      Monster: [],
      Dice: [],
      TechFetch: [],
      CareerBuilder: [],
      CWJobs: [],
    });

  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspace = useWorkspaceContext();

  const [open, setOpen] = useState(false);
  const handleDrawerOpen = () => setOpen(true);

  const [selectedJobBoardName, setSelectedJobBoardName] = useState<string>('');

  const openJobBoard = (jobBoardName: string) =>
    navigate(`/jobs/${jobData.id}/publish/${jobBoardName}`);

  const handleOnClick = (name: string, accountsLength: number) => {
    if (name === 'monster') {
      amplitude('Job_publish_monster', {
        clickedOn: 'Monster',
        pageName: 'Publish jobs',
        pageUrl: window.location.href,
        workspaceName: workspace.name,
        planType: workspace.planType,
        interfaceType: 'Web',
      });
    }
    setSelectedJobBoardName(name);
    if (accountsLength > 0) {
      openJobBoard(name);
    } else {
      handleDrawerOpen();
    }
  };

  useEffect(() => {
    const setAllJobBoard = async () => {
      const allAccounts = await getJobBoards('Monster,Dice,TechFetch,CareerBuilder,CWJobs');

      const filterByJobBoardName = (name: string) =>
        allAccounts.filter((account: IJobBoard) => account.jobBoardName === name);

      setPremiumJobBoardAccountResponse({
        Monster: filterByJobBoardName('Monster'),
        Dice: filterByJobBoardName('Dice'),
        TechFetch: filterByJobBoardName('TechFetch'),
        CareerBuilder: filterByJobBoardName('CareerBuilder'),
        CWJobs: filterByJobBoardName('CWJobs'),
      });
    };
    setAllJobBoard();
  }, []);

  const allAccountCounts = (response: IJobBoard[], loggedInUser: string) =>
    response?.filter(
      (account) =>
        account.jobBoardSharedUsers &&
        account.jobBoardSharedUsers?.filter((sharedAccount) => sharedAccount.email == loggedInUser)
          .length > 0
    );

  const sharedUserCount = (response: IJobBoard[], loggedInUser: string) => {
    let count = 0;
    response.forEach((jobBoard) => {
      jobBoard?.jobBoardSharedUsers?.forEach((jobBoardSharedUser) => {
        if (jobBoardSharedUser.email == loggedInUser)
          count = count + jobBoardSharedUser.maxPostingsPerMonth;
      });
    });
    return count;
  };

  const premiumJobBoardCardDetails = [
    { name: 'Monster', response: premiumJobBoardAccountResponse.Monster, imgSrc: MonsterIcon },
    {
      name: 'TechFetch',
      response: premiumJobBoardAccountResponse.TechFetch,
      imgSrc: TechFetchIcon,
    },
    { name: 'Dice', response: premiumJobBoardAccountResponse.Dice, imgSrc: DiceIcon },
    {
      name: 'CareerBuilder',
      response: premiumJobBoardAccountResponse.CareerBuilder,
      imgSrc: CareerBuilderIcon,
    },
    { name: 'CWJobs', response: premiumJobBoardAccountResponse.CWJobs, imgSrc: CwJobsIcon },
  ];

  return (
    <>
      {showPremiumJobPosting && (
        <>
          <Paper variant="outlined" sx={{ p: 3, bgcolor: WsColors.TheBlues800 }}>
            <Grid item xs={12} pb={5}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY200}>
                Premium Job Boards
              </WsTypography>
            </Grid>
            <Grid container rowSpacing={2} columnSpacing={2} pt={2}>
              {premiumJobBoardCardDetails.map((board) => (
                <Grid
                  item
                  xs={6}
                  sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                  key={board.name}
                >
                  <ActivateFeature feature="JS_EXTERNAL_JOB_POSTING_PREMIUM">
                    <WsCard
                      middleComponent={<img src={board.imgSrc} alt={board.name} height={50} />}
                      cardType={CardVariant.WITH_OUT_BG}
                      title={board.name}
                      onClick={() => handleOnClick(board.name, board.response.length)}
                      description={`Total Credits: ${sharedUserCount(
                        board.response,
                        loggedInUser?.email ?? ''
                      )}`}
                      buttonText={
                        allAccountCounts(board.response, loggedInUser.email ?? '').length >= 0
                          ? `${
                              allAccountCounts(board.response, loggedInUser.email ?? '').length
                            } accounts`
                          : 'AddAccount'
                      }
                      isButton={false}
                      buttonSize="small"
                    />
                  </ActivateFeature>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12} container justifyContent="center" pt={4}>
              <ActivateFeature feature="JS_EXTERNAL_JOB_POSTING_PREMIUM">
                <StyledButton
                  variant="text"
                  onClick={() => navigate('/job-boards/')}
                  startIcon={<SettingsIconAlt />}
                  size="large"
                >
                  Configure account
                </StyledButton>
              </ActivateFeature>
            </Grid>
            <PublishAddAccountDrawer
              openDrawer={open}
              onClose={() => setOpen(false)}
              jobBoardName={selectedJobBoardName}
            />
          </Paper>
        </>
      )}
    </>
  );
};

export default PremiumJobBoardAccounts;
